import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import useDocumentMeta from '../../utils/useDocumentMeta';
import TermsModal from '../../components/Modals/TermsModal/TermsModal';
import PublicNavbar from '../../components/PublicNavbar/PublicNavbar';
import '../NewLandingPage/LandingPage.css';
import './Contacts.css';

const ClipboardIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
    </svg>
);

const MailIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
        <polyline points="22,6 12,13 2,6" />
    </svg>
);

const ClockIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
    </svg>
);

const CheckCircleIcon = () => (
    <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
        <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
);

const Contacts = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
    const [errors, setErrors] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [sending, setSending] = useState(false);
    const [submitError, setSubmitError] = useState('');
    useDocumentMeta('seo-contacts-title', 'seo-contacts-desc', { canonicalPath: '/contacts' });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: '' }));
        setSubmitError('');
    };

    const validate = () => {
        const tmp = {};
        if (!formData.name.trim()) tmp.name = t('cp-form-error-name');
        if (!formData.email.trim()) tmp.email = t('cp-form-error-email');
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) tmp.email = t('cp-form-error-email-invalid');
        if (!formData.message.trim()) tmp.message = t('cp-form-error-message');
        setErrors(tmp);
        return Object.keys(tmp).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        try {
            setSending(true);
            setSubmitError('');
            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, formData);
            setSubmitted(true);
        } catch (err) {
            if (err.response?.status === 429) {
                setSubmitError(t('cp-form-error-rate-limit'));
            } else {
                setSubmitError(t('cp-form-error-server'));
            }
        } finally {
            setSending(false);
        }
    };

    return (
        <>
        <PublicNavbar />
        <div className="cp nlp">

            {/* ── HERO ── */}
            <header className="cp-hero">
                <div className="nlp-container">
                    <span className="nlp-badge nlp-badge-blue">{t('cp-badge')}</span>
                    <h1 className="cp-hero-title">{t('cp-hero-title')}</h1>
                    <p className="cp-hero-subtitle">{t('cp-hero-subtitle')}</p>
                </div>
            </header>

            {/* ── CONTACT SECTION ── */}
            <section className="cp-section">
                <div className="nlp-container">
                    <div className="cp-grid">

                        {/* Info Column */}
                        <div className="cp-info-col">
                            <h2 className="cp-info-title">{t('cp-info-title')}</h2>

                            <a href={`mailto:${t('cp-info-email-value')}`} className="cp-info-card cp-info-card-link">
                                <div className="cp-info-card-icon cp-icon-orange"><MailIcon /></div>
                                <div>
                                    <div className="cp-info-card-label">{t('cp-info-email-label')}</div>
                                    <div className="cp-info-card-value">{t('cp-info-email-value')}</div>
                                </div>
                            </a>

                            <div className="cp-info-card">
                                <div className="cp-info-card-icon cp-icon-green"><ClockIcon /></div>
                                <div>
                                    <div className="cp-info-card-label">{t('cp-info-hours-title')}</div>
                                    <div className="cp-info-card-value">{t('cp-info-hours-body')}</div>
                                </div>
                            </div>

                            <div className="cp-about-block">
                                <h3 className="cp-about-title">{t('cp-info-about-title')}</h3>
                                <p className="cp-about-body">{t('cp-info-about-body')}</p>
                            </div>
                        </div>

                        {/* Form Column */}
                        <div className="cp-form-col">
                            <div className="cp-form-card">
                                <h2 className="cp-form-title">{t('cp-form-title')}</h2>
                                {submitted ? (
                                    <div className="cp-success">
                                        <CheckCircleIcon />
                                        <p>{t('cp-form-success')}</p>
                                    </div>
                                ) : (
                                    <form onSubmit={handleSubmit} className="cp-form" noValidate>
                                        <div className="cp-form-row">
                                            <div className="cp-form-field">
                                                <label htmlFor="cp-name">{t('cp-form-name')}</label>
                                                <input
                                                    id="cp-name"
                                                    name="name"
                                                    type="text"
                                                    value={formData.name}
                                                    onChange={handleChange}
                                                    placeholder={t('cp-form-name-placeholder')}
                                                    maxLength={100}
                                                    className={errors.name ? 'cp-input-error' : ''}
                                                />
                                                {errors.name && <span className="cp-field-error">{errors.name}</span>}
                                            </div>
                                            <div className="cp-form-field">
                                                <label htmlFor="cp-email">{t('cp-form-email')}</label>
                                                <input
                                                    id="cp-email"
                                                    name="email"
                                                    type="email"
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                    placeholder={t('cp-form-email-placeholder')}
                                                    maxLength={254}
                                                    className={errors.email ? 'cp-input-error' : ''}
                                                />
                                                {errors.email && <span className="cp-field-error">{errors.email}</span>}
                                            </div>
                                        </div>
                                        <div className="cp-form-field">
                                            <label htmlFor="cp-subject">
                                                {t('cp-form-subject')} <span className="cp-optional">({t('cp-form-optional')})</span>
                                            </label>
                                            <input
                                                id="cp-subject"
                                                name="subject"
                                                type="text"
                                                value={formData.subject}
                                                onChange={handleChange}
                                                placeholder={t('cp-form-subject-placeholder')}
                                                maxLength={150}
                                            />
                                        </div>
                                        <div className="cp-form-field">
                                            <label htmlFor="cp-message">{t('cp-form-message')}</label>
                                            <textarea
                                                id="cp-message"
                                                name="message"
                                                value={formData.message}
                                                onChange={handleChange}
                                                placeholder={t('cp-form-message-placeholder')}
                                                maxLength={1000}
                                                className={errors.message ? 'cp-input-error' : ''}
                                            />
                                            {errors.message && <span className="cp-field-error">{errors.message}</span>}
                                        </div>
                                        <button type="submit" className="cp-form-submit" disabled={sending}>
                                            {sending ? t('cp-form-sending') : t('cp-form-submit')}
                                        </button>
                                        {submitError && <p className="cp-submit-error">{submitError}</p>}
                                    </form>
                                )}
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            {/* ── FOOTER ── */}
            <footer className="nlp-footer">
                <div className="nlp-container">
                    <div className="nlp-footer-inner">
                        <div className="nlp-footer-logo">
                            <div className="nlp-footer-logo-icon"><ClipboardIcon /></div>
                            <span className="nlp-footer-logo-text">Dietist<span>Pro</span></span>
                        </div>
                        <div className="nlp-footer-links">
                            <a href="#" onClick={(e) => { e.preventDefault(); setShowTermsModal(true); }}>{t('nlp-footer-privacy')}</a>
                            <a href="#" onClick={(e) => { e.preventDefault(); setShowTermsModal(true); }}>{t('nlp-footer-terms')}</a>
                        </div>
                        <span className="nlp-footer-copy">{t('nlp-footer-copy')}</span>
                    </div>
                </div>
            </footer>
        </div>

        <TermsModal open={showTermsModal} onClose={() => setShowTermsModal(false)} />
        </>
    );
};

export default Contacts;
